import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * sessionStorage key for persisting in-progress quiz answers across a refresh.
 * @constant {string}
 */
const STORAGE_KEY = 'arcticpaw-quiz-state';

/**
 * Scoring config: each answered dimension contributes weighted points toward candidate products.
 * A product enters the live kit once its cumulative score crosses KIT_THRESHOLD. Vest is weighted
 * to lead or tie-lead across nearly every path (it's the anchor product) rather than being
 * hardcoded as "always included" — the kit is genuinely score-derived. BottlePup is never scored
 * here; it's appended as the fixed free-gift line once Vest qualifies (see #computeKit).
 *
 * Tune freely — this is the one file that controls what gets recommended.
 * @type {Record<string, Record<string, Record<string, number>>>}
 */
const RULES = {
  climate: {
    hot: { vest: 2, aquago_pro: 3, mat: 2 },
    warm: { vest: 2, aquago: 2 },
    mild: { vest: 1, mat: 1 },
  },
  activity: {
    veryActive: { vest: 3, aquago_pro: 2 },
    moderate: { vest: 2, aquago: 1 },
    low: { vest: 1, mat: 2 },
  },
  coat: {
    double: { vest: 2, mat: 2 },
    single: { vest: 1 },
    unsure: { vest: 1 },
  },
  age: {
    senior: { mat: 3, vest: 1 },
    adult: { vest: 1 },
  },
};

/** @constant {number} */
const KIT_THRESHOLD = 2;

/**
 * @typedef {{ id: string, size: string, price: number, price_formatted: string, compare_at_price: number | null, compare_at_price_formatted: string | null, available: boolean }} VestVariant
 * @typedef {{ id: string, title: string, url: string, image: string, variants: VestVariant[] }} VestCatalogEntry
 * @typedef {{ variantId: string, title: string, url: string, image: string, price: number, price_formatted: string, compare_at_price: number | null, compare_at_price_formatted: string | null, available: boolean }} CatalogEntry
 * @typedef {{ vest: VestCatalogEntry, mat: CatalogEntry, aquago: CatalogEntry, aquago_pro: CatalogEntry, bottlepup: CatalogEntry }} Catalog
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} screens
 * @property {HTMLElement} progressFill
 * @property {HTMLElement} liveRegion
 * @property {HTMLInputElement} dogNameInput
 * @property {HTMLButtonElement} startButton
 * @property {HTMLButtonElement} backButton
 * @property {HTMLElement} sizeOptions
 * @property {HTMLElement} exactMeasurementFields
 * @property {HTMLInputElement} chestInput
 * @property {HTMLElement} revealScreen
 * @property {HTMLElement} saleBanner
 * @property {HTMLElement} revealHeading
 * @property {HTMLElement} revealRationale
 * @property {HTMLElement} revealList
 * @property {HTMLElement} revealSubtotal
 * @property {HTMLElement} revealSavings
 * @property {HTMLButtonElement} ctaButton
 * @property {HTMLElement} ctaStatus
 * @extends {Component<Refs>}
 */
class ArcticpawQuizComponent extends Component {
  requiredRefs = [
    'screens',
    'progressFill',
    'liveRegion',
    'startButton',
    'backButton',
    'revealScreen',
    'saleBanner',
    'revealHeading',
    'revealRationale',
    'revealList',
    'revealSubtotal',
    'revealSavings',
    'ctaButton',
    'ctaStatus',
  ];

  /**
   * Flat navigation history of screen indexes visited (into `this.refs.screens`), so Back always
   * has somewhere real to go regardless of auto-advance. Seeded with the intro screen (index 0)
   * since that's what's visible on first render, before any navigation has happened.
   * @type {number[]}
   */
  #history = [0];

  /** @type {number} */
  #currentScreenIndex = 0;

  /**
   * Recorded answers, keyed by question_key (climate/activity/coat/age) plus `size`/`chest_cm`.
   * @type {Record<string, string>}
   */
  #answers = {};

  /** @type {boolean} */
  #usingExactMeasurement = false;

  /**
   * True while the reveal screen is showing. The reveal is a "beyond the last screen" state, not a
   * member of `this.refs.screens`, so navigation has to hide it explicitly and Back has to treat
   * it as a special case (return to the size step without consuming a history entry).
   * @type {boolean}
   */
  #onReveal = false;

  /** @type {Catalog | null} */
  #catalog = null;

  /**
   * Resolved bundle from the last reveal, cached so addKitToCart doesn't need to recompute.
   * @type {Array<{ role: string, variantId: string, title: string, image: string, price: number, priceFormatted: string, qty: number, free: boolean, available: boolean }>}
   */
  #kitLines = [];

  connectedCallback() {
    super.connectedCallback();
    this.#catalog = this.#parseCatalog();
    this.#restoreState();
    this.#preloadNextImage();
  }

  /** @returns {Catalog | null} */
  #parseCatalog() {
    const script = this.querySelector('script[data-apq-catalog]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Cooling quiz: failed to parse catalog data', error);
      return null;
    }
  }

  // ---------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------

  #saveState() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          answers: this.#answers,
          currentScreenIndex: this.#currentScreenIndex,
          history: this.#history,
          usingExactMeasurement: this.#usingExactMeasurement,
          onReveal: this.#onReveal,
        })
      );
    } catch (error) {
      // sessionStorage can throw in private-browsing contexts; losing persistence is fine.
    }
  }

  #restoreState() {
    let saved;

    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch (error) {
      saved = null;
    }

    if (!saved || typeof saved.currentScreenIndex !== 'number') return;

    this.#answers = saved.answers ?? {};
    this.#history = Array.isArray(saved.history) && saved.history.length > 0 ? saved.history : [0];
    this.#usingExactMeasurement = Boolean(saved.usingExactMeasurement);

    if (this.refs.dogNameInput && this.#answers.dog_name) {
      this.refs.dogNameInput.value = this.#answers.dog_name;
    }

    // Refreshing while on the reveal restores straight back to it (currentScreenIndex still points
    // at the size step, which is where Back should land). Don't re-fire the analytics beacon.
    if (saved.onReveal) {
      this.#currentScreenIndex = saved.currentScreenIndex;
      this.#reveal({ sendBeacon: false, animate: false });
      return;
    }

    this.#goToScreen(saved.currentScreenIndex, { recordHistory: false, restoring: true });
  }

  // ---------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------

  start() {
    this.#answers.dog_name = this.refs.dogNameInput?.value?.trim() || '';
    this.#goToScreen(1);
  }

  goBack() {
    // From the reveal, Back returns to the last real screen (the size step) rather than popping
    // history — reveal isn't a history stop of its own, so popping would skip the size step.
    if (this.#onReveal) {
      this.#goToScreen(this.#currentScreenIndex, { recordHistory: false });
      return;
    }

    if (this.#history.length <= 1) return;

    this.#history.pop();
    const previousIndex = this.#history[this.#history.length - 1];
    this.#goToScreen(previousIndex, { recordHistory: false });
  }

  /**
   * @param {Event} event
   */
  selectOption(event) {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-option]') : null);
    if (!button) return;

    const screen = button.closest('[data-question-key]');
    const questionKey = screen instanceof HTMLElement ? screen.dataset.questionKey : undefined;
    const value = button.dataset.value;
    if (!questionKey || value === undefined) return;

    this.#answers[questionKey] = value;
    this.#markSelected(button);
    this.#saveState();

    this.#autoAdvance();
  }

  /** @param {Event} event */
  selectSize(event) {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-size-option]') : null);
    if (!button?.dataset.value) return;

    this.#answers.size = button.dataset.value;
    delete this.#answers.chest_cm;
    this.#markSelected(button, this.refs.sizeOptions);
    this.#saveState();

    this.#autoAdvance();
  }

  toggleExactMeasurement() {
    this.#usingExactMeasurement = !this.#usingExactMeasurement;
    this.refs.exactMeasurementFields.hidden = !this.#usingExactMeasurement;
  }

  useExactMeasurement() {
    const chestCm = Number(this.refs.chestInput?.value);
    if (!chestCm) {
      this.refs.chestInput?.reportValidity();
      return;
    }

    this.#answers.chest_cm = String(chestCm);
    this.#answers.size = sizeFromChest(chestCm).size;

    // Clear any previously-tapped size highlight so the visual state can't contradict the size the
    // exact measurement just computed.
    this.refs.sizeOptions?.querySelectorAll('.apq__option').forEach((el) => {
      el.classList.remove('apq__option--selected');
      el.setAttribute('aria-pressed', 'false');
    });

    this.#saveState();

    this.#autoAdvance();
  }

  /**
   * Marks a single button as selected within its option group (single-select visual state).
   * @param {HTMLElement} button
   * @param {Element} [scope]
   */
  #markSelected(button, scope) {
    const group = scope ?? button.closest('.apq__options');
    group?.querySelectorAll('[aria-pressed]').forEach((el) => el.setAttribute('aria-pressed', 'false'));
    group?.querySelectorAll('.apq__option').forEach((el) => el.classList.remove('apq__option--selected'));
    button.classList.add('apq__option--selected');
    button.setAttribute('aria-pressed', 'true');

    if (!prefersReducedMotion()) {
      button.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }],
        { duration: 260, easing: 'ease-out' }
      );
    }
  }

  #autoAdvance() {
    const delay = prefersReducedMotion() ? 0 : 450;
    setTimeout(() => this.#goToScreen(this.#currentScreenIndex + 1), delay);
  }

  /**
   * @param {number} index
   * @param {{ recordHistory?: boolean, restoring?: boolean }} [options]
   */
  #goToScreen(index, options = {}) {
    const { recordHistory = true, restoring = false } = options;
    const { screens } = this.refs;

    if (index >= screens.length) {
      this.#reveal();
      return;
    }

    // Leaving (or never entering) the reveal state: make sure the reveal screen is hidden. Without
    // this, pressing Back from the finished state leaves the kit rendered underneath the question.
    this.#onReveal = false;
    this.refs.revealScreen.hidden = true;

    const previousIndex = this.#currentScreenIndex;
    const direction = index >= previousIndex ? 1 : -1;
    const outgoing = restoring ? null : screens[previousIndex];
    const incoming = screens[index];

    this.#currentScreenIndex = index;
    if (recordHistory) this.#history.push(index);

    this.refs.backButton.hidden = index <= 0;
    this.#updateProgress(index, screens.length);
    this.#preloadNextImage();

    if (!restoring) this.#saveState();

    this.#transitionScreens(outgoing, incoming, direction, restoring);
  }

  /**
   * Directional slide+fade between the outgoing and incoming screen via the Web Animations API.
   * Falls back to an instant swap under prefers-reduced-motion or when restoring from
   * sessionStorage (no transition needed for a page that's just loading).
   * @param {HTMLElement | null} outgoing
   * @param {HTMLElement} incoming
   * @param {1 | -1} direction
   * @param {boolean} restoring
   */
  #transitionScreens(outgoing, incoming, direction, restoring) {
    const { screens } = this.refs;

    if (prefersReducedMotion() || restoring) {
      screens.forEach((screen) => {
        screen.hidden = screen !== incoming;
      });
      this.#focusHeading(incoming);
      return;
    }

    screens.forEach((screen) => {
      if (screen !== outgoing && screen !== incoming) screen.hidden = true;
    });

    incoming.hidden = false;
    const distance = 24 * direction;

    if (outgoing && outgoing !== incoming) {
      const outAnimation = outgoing.animate(
        [
          { opacity: 1, transform: 'translateX(0)' },
          { opacity: 0, transform: `translateX(${-distance}px)` },
        ],
        { duration: 260, easing: 'ease-in' }
      );
      outAnimation.onfinish = () => {
        outgoing.hidden = true;
      };
    }

    incoming.animate(
      [
        { opacity: 0, transform: `translateX(${distance}px)` },
        { opacity: 1, transform: 'translateX(0)' },
      ],
      { duration: 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );

    this.#focusHeading(incoming);
  }

  /** @param {HTMLElement | null} screen */
  /** @param {HTMLElement | null} screenOrHeading A screen to find a heading within, or a heading element itself. */
  #focusHeading(screenOrHeading) {
    const heading =
      screenOrHeading?.matches('h1, h2') ? screenOrHeading : screenOrHeading?.querySelector('h1, h2');

    if (heading instanceof HTMLElement) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: false });
    }
  }

  /**
   * @param {number} index
   * @param {number} total
   */
  #updateProgress(index, total) {
    const percent = total > 1 ? Math.round((index / (total - 1)) * 100) : 0;
    this.refs.progressFill.style.width = `${percent}%`;
    this.refs.liveRegion.textContent = `Question ${index} of ${total - 1}`;
  }

  #preloadNextImage() {
    const nextScreen = this.refs.screens[this.#currentScreenIndex + 1];
    const nextImage = nextScreen?.querySelector('img');
    if (nextImage instanceof HTMLImageElement && nextImage.currentSrc) {
      new Image().src = nextImage.currentSrc;
    }
  }

  // ---------------------------------------------------------------------
  // Kit assembly
  // ---------------------------------------------------------------------

  /**
   * Scores every answered dimension against RULES and returns the qualifying product roles,
   * highest score first. AquaGo Pro supersedes standard AquaGo when both would otherwise
   * qualify (never recommend both). Vest is scored like everything else, not hardcoded in —
   * it just tends to lead because of how the weights are tuned.
   * @returns {string[]}
   */
  #computeKitRoles() {
    /** @type {Record<string, number>} */
    const scores = { vest: 0, mat: 0, aquago: 0, aquago_pro: 0 };

    for (const [questionKey, value] of Object.entries(this.#answers)) {
      const weights = RULES[questionKey]?.[value];
      if (!weights) continue;

      for (const [role, points] of Object.entries(weights)) {
        scores[role] = (scores[role] ?? 0) + points;
      }
    }

    if (scores.aquago_pro > 0 && scores.aquago > 0) {
      if (scores.aquago_pro >= scores.aquago) scores.aquago = 0;
      else scores.aquago_pro = 0;
    }

    return Object.entries(scores)
      .filter(([, score]) => score >= KIT_THRESHOLD)
      .sort((a, b) => b[1] - a[1])
      .map(([role]) => role);
  }

  /**
   * Resolves the scored roles into real catalog lines: the vest's variant is picked by the
   * answered size, everything else is a single-variant product. BottlePup rides along as a fixed
   * free line whenever an available Vest qualifies — matching the store's "free BottlePup with
   * every Cooling Vest" discount, which is what actually zeroes its price at cart/checkout; this
   * is display + cart-line bookkeeping only, never a price override.
   * @returns {Array<{ role: string, variantId: string, title: string, image: string, price: number, priceFormatted: string, compareAtPrice: number | null, compareAtPriceFormatted: string | null, qty: number, free: boolean, available: boolean }>}
   */
  #resolveKitLines() {
    if (!this.#catalog) return [];

    const catalog = this.#catalog;
    const roles = this.#computeKitRoles();
    const lines = [];

    for (const role of roles) {
      if (role === 'vest') {
        const variant = catalog.vest.variants.find((v) => v.size === this.#answers.size);
        if (!variant) continue;

        lines.push({
          role: 'vest',
          variantId: variant.id,
          title: `${catalog.vest.title} (${variant.size})`,
          image: catalog.vest.image,
          price: variant.price,
          priceFormatted: variant.price_formatted,
          compareAtPrice: variant.compare_at_price,
          compareAtPriceFormatted: variant.compare_at_price_formatted,
          qty: 1,
          free: false,
          available: variant.available,
        });
      } else {
        const entry = catalog[/** @type {'mat' | 'aquago' | 'aquago_pro'} */ (role)];
        if (!entry) continue;

        lines.push({
          role,
          variantId: entry.variantId,
          title: entry.title,
          image: entry.image,
          price: entry.price,
          priceFormatted: entry.price_formatted,
          compareAtPrice: entry.compare_at_price,
          compareAtPriceFormatted: entry.compare_at_price_formatted,
          qty: 1,
          free: false,
          available: entry.available,
        });
      }
    }

    const vestLine = lines.find((line) => line.role === 'vest');
    if (vestLine?.available && catalog.bottlepup) {
      lines.push({
        role: 'bottlepup',
        variantId: catalog.bottlepup.variantId,
        title: catalog.bottlepup.title,
        image: catalog.bottlepup.image,
        price: catalog.bottlepup.price,
        priceFormatted: catalog.bottlepup.price_formatted,
        compareAtPrice: catalog.bottlepup.compare_at_price,
        compareAtPriceFormatted: catalog.bottlepup.compare_at_price_formatted,
        qty: 1,
        free: true,
        available: catalog.bottlepup.available,
      });
    }

    return lines;
  }

  /**
   * Renders the fully-resolved kit into the reveal screen in one pass, staggered entrance + soft
   * glow per item — the "opening a gift" moment. The kit isn't shown incrementally during the
   * quiz; it assembles all at once right here, so nothing leaks before the reveal.
   */
  #renderRevealKit() {
    if (!this.#catalog) return;

    this.#kitLines = this.#resolveKitLines();
    const { revealList } = this.refs;
    revealList.textContent = '';

    this.#kitLines.forEach((line, index) => {
      const item = this.#buildKitItem(line);
      revealList.append(item);
      this.#animateKitItemIn(item, index);
    });

    this.refs.saleBanner.textContent = this.dataset.saleBanner || '';
    this.refs.revealRationale.textContent = this.#buildRationale();
    this.#renderSubtotal();
  }

  /**
   * @param {{ role: string, title: string, image: string, priceFormatted: string, compareAtPriceFormatted: string | null, free: boolean, available: boolean }} line
   * @returns {HTMLLIElement}
   */
  #buildKitItem(line) {
    const li = document.createElement('li');
    li.className = 'apq__kit-item';
    if (line.free) li.classList.add('apq__kit-item--free');
    if (!line.available) li.classList.add('apq__kit-item--unavailable');
    li.dataset.kitRole = line.role;

    const priceMarkup = line.free
      ? 'Free'
      : line.compareAtPriceFormatted
        ? `<s class="apq__kit-item-compare-price">${line.compareAtPriceFormatted}</s> ${line.priceFormatted}`
        : line.priceFormatted;

    li.innerHTML = `
      ${line.image ? `<img class="apq__kit-item-image" src="${line.image}" alt="" width="40" height="40" loading="lazy">` : ''}
      <span class="apq__kit-item-info">
        <span class="apq__kit-item-title">${line.title}</span>
        ${!line.available ? '<span class="apq__kit-item-status">Currently sold out</span>' : ''}
      </span>
      <span class="apq__kit-item-price">${priceMarkup}</span>
    `;
    return li;
  }

  /**
   * A templated (non-AI) personalization sentence — uses the dog's name and the answered
   * climate/activity to sound specific, but never claims a size or product choice beyond what
   * the scoring engine actually produced.
   * @returns {string}
   */
  #buildRationale() {
    const dogName = this.#answers.dog_name?.trim();
    const possessive = dogName ? `${dogName}'s` : "Your dog's";

    /** @type {Record<string, string>} */
    const climateLabels = { hot: 'scorching summers', warm: 'warm summers', mild: 'milder summers' };
    /** @type {Record<string, string>} */
    const activityLabels = { veryActive: 'big adventures', moderate: 'daily walks', low: 'relaxed days at home' };

    const context = [climateLabels[this.#answers.climate], activityLabels[this.#answers.activity]]
      .filter(Boolean)
      .join(' and ');

    return context
      ? `${possessive} kit is built around ${context} — sized and matched from what you told us.`
      : `${possessive} kit is sized and matched from what you told us.`;
  }

  #renderSubtotal() {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';

    // Shopify's Liquid `variant.price` (and therefore the catalog data island built from it) is
    // already in the shop's minor currency unit (cents), matching what formatMoney expects — no
    // extra *100 conversion here.
    const priceableLines = this.#kitLines.filter((line) => line.available && !line.free);

    const subtotalMinorUnits = priceableLines.reduce((sum, line) => sum + line.price * line.qty, 0);
    this.refs.revealSubtotal.textContent = formatMoney(Math.round(subtotalMinorUnits), format, currency);

    const savingsMinorUnits = priceableLines
      .filter((line) => line.compareAtPrice)
      .reduce((sum, line) => sum + (/** @type {number} */ (line.compareAtPrice) - line.price) * line.qty, 0);

    if (savingsMinorUnits > 0) {
      this.refs.revealSavings.textContent = `You saved ${formatMoney(Math.round(savingsMinorUnits), format, currency)}`;
      this.refs.revealSavings.hidden = false;
    } else {
      this.refs.revealSavings.hidden = true;
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {number} staggerIndex
   */
  #animateKitItemIn(item, staggerIndex) {
    if (prefersReducedMotion()) return;

    const delay = staggerIndex * 80;

    item.animate(
      [
        { opacity: 0, transform: 'translateY(8px) scale(0.96)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 420, delay, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'backwards' }
    );

    item.animate(
      [
        { boxShadow: '0 0 0 0 rgb(var(--apq-accent-rgb) / 0.35)' },
        { boxShadow: '0 0 0 10px rgb(var(--apq-accent-rgb) / 0)' },
      ],
      { duration: 600, delay: delay + 200, easing: 'ease-out' }
    );
  }

  /** @param {{ sendBeacon?: boolean, animate?: boolean }} [options] */
  #reveal(options = {}) {
    const { sendBeacon = true, animate = true } = options;
    const reduced = prefersReducedMotion();

    this.#onReveal = true;

    this.refs.screens.forEach((screen) => {
      screen.hidden = true;
    });
    this.refs.revealScreen.hidden = false;
    // Keep Back available so the user can return to the size step and change answers.
    this.refs.backButton.hidden = false;
    this.#renderRevealKit();

    if (!reduced && animate) {
      this.refs.revealScreen.animate(
        [
          { opacity: 0, transform: 'translateY(12px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }

    this.refs.liveRegion.textContent = 'Your ArcticPaw Kit is ready';
    this.#focusHeading(this.refs.revealHeading);
    this.#saveState();

    // Don't re-fire analytics when the reveal is just being restored from sessionStorage on refresh.
    if (sendBeacon) this.#sendAnalyticsBeacon();
  }

  /**
   * Fire-and-forget analytics/retargeting beacon — fired once the reveal has rendered, never
   * blocks or delays it, and fails completely silently (no console noise, no user-facing error).
   * Uses sendBeacon where available since it survives the page continuing on to /cart.
   */
  #sendAnalyticsBeacon() {
    const url = this.dataset.analyticsWebhookUrl;
    if (!url) return;

    try {
      const payload = JSON.stringify({
        answers: this.#answers,
        kit: this.#kitLines.map((line) => ({ role: line.role, free: line.free, available: line.available })),
        timestamp: new Date().toISOString(),
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(
          () => {}
        );
      }
    } catch (error) {
      // Analytics is best-effort only; a failure here must never surface to the user.
    }
  }

  // ---------------------------------------------------------------------
  // Cart
  // ---------------------------------------------------------------------

  async addKitToCart() {
    const items = this.#kitLines
      .filter((line) => line.available)
      .map((line) => ({ id: Number(line.variantId), quantity: line.qty }));

    if (items.length === 0) {
      this.refs.ctaStatus.textContent = "We couldn't add this kit — those items are currently sold out.";
      return;
    }

    this.refs.ctaButton.disabled = true;
    this.refs.ctaStatus.textContent = '';

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const el of cartItemsComponents) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });

      const result = await response.json();

      if (result.status) {
        this.refs.ctaStatus.textContent = result.message || 'Something went wrong adding this to your cart.';
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(result, this.id || 'arcticpaw-quiz', {
          source: 'arcticpaw-quiz-component',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
        })
      );

      this.refs.ctaStatus.textContent = 'Added to your cart.';
    } catch (error) {
      console.error('Cooling quiz: add to cart failed', error);
      this.refs.ctaStatus.textContent = 'Something went wrong adding this to your cart. Please try again.';
    } finally {
      this.refs.ctaButton.disabled = false;
    }
  }
}

/**
 * Deterministic chest_cm -> vest size lookup, used only for the optional "exact measurement"
 * override. Mirrors the table from the previous build; TIE_BREAK = "up" (a too-small vest is the
 * costlier mistake).
 * @param {number} chestCm
 * @returns {{ size: string }}
 */
function sizeFromChest(chestCm) {
  if (chestCm < 30) return { size: 'S' };
  if (chestCm > 110) return { size: '3XL' };

  const bands = [
    { max: 38, size: 'S' },
    { max: 48, size: 'M' },
    { max: 59, size: 'L' },
    { max: 73, size: 'XL' },
    { max: 83, size: '2XL' },
    { max: 110, size: '3XL' },
  ];

  return { size: (bands.find((b) => chestCm <= b.max) ?? bands[bands.length - 1]).size };
}

if (!customElements.get('arcticpaw-quiz-component')) {
  customElements.define('arcticpaw-quiz-component', ArcticpawQuizComponent);
}
